import { motion, AnimatePresence } from 'motion/react';
import { useMemo, useState } from 'react';
import { Pause, Play, Filter, Activity as ActivityIcon, X } from 'lucide-react';
import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PageContainer, PageHeader } from '@/components/viz/PageHeader';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
} from '@/components/ui/primitives';
import { useSseStream } from '@/lib/useSseStream';
import { FocttaCoreBadge } from '@/components/foctta-core-badge';

/**
 * Raw row shape emitted by /v1/activity/events/stream (mirrors the
 * ClickHouse activity_events columns; see
 * apps/api-controller/src/modules/activity/clickhouse.ts ActivityEventRow).
 */
interface ActivityEventRowDto {
  tenant_id: string;
  asset_id: string;
  occurred_at: string;
  actor_user_id: string;
  actor_role: string;
  session_id: string;
  application: string;
  statement_hash: string;
  statement_type: string;
  tables_touched: string[];
  pii_categories_touched: string[];
  rows_returned: number;
  duration_ms: number;
  decision: 'allow' | 'mask' | 'step_up' | 'block';
  decision_explanation: string;
  severity: string;
}

interface Event {
  id: string;
  ts: string;
  actor: string;
  statementHash: string;
  type: 'SELECT' | 'INSERT' | 'UPDATE' | 'DELETE' | 'EXPORT';
  table: string;
  pii: string[];
  rows: number;
  decision: 'allow' | 'mask' | 'step_up' | 'block';
  latency: number;
}

function toViewModel(raw: ActivityEventRowDto, sseEventId: string): Event {
  const ts = new Date(raw.occurred_at);
  return {
    id: sseEventId || raw.session_id || `evt-${ts.getTime()}`,
    ts:
      ts.toLocaleTimeString('en-IN', { hour12: false }) +
      '.' +
      String(ts.getMilliseconds()).padStart(3, '0'),
    actor: raw.actor_user_id,
    statementHash: raw.statement_hash.slice(0, 12),
    type: (raw.statement_type ?? 'SELECT').toUpperCase() as Event['type'],
    table: raw.tables_touched[0] ?? '-',
    pii: raw.pii_categories_touched ?? [],
    rows: raw.rows_returned,
    decision: raw.decision,
    latency: raw.duration_ms,
  };
}

/**
 * Filter state for the live stream. All filters are CLIENT-SIDE - they
 * hide/show rows from the in-memory SSE buffer rather than restarting
 * the connection. This preserves the live feed while letting the
 * operator narrow what they see (Stream is a tile page; the real
 * forensic search lives on Timeline / Decisions).
 *
 * Tier UX-fix 2026-05-26 - closes OBS-8 (dead Filter button per
 * DAM-DEEP-INVESTIGATION P0 #5). Backend QueryEventsSchema supports
 * server-side filtering too; that's a future enhancement when the
 * buffer-based filter proves insufficient.
 */
interface StreamFilters {
  readonly decisions: ReadonlySet<'allow' | 'mask' | 'step_up' | 'block'>;
  readonly actorContains: string;
  readonly tableContains: string;
  readonly piiOnly: boolean;
}

const EMPTY_FILTERS: StreamFilters = {
  decisions: new Set(),
  actorContains: '',
  tableContains: '',
  piiOnly: false,
};

function countActiveFilters(f: StreamFilters): number {
  let n = 0;
  if (f.decisions.size > 0) n += 1;
  if (f.actorContains.trim()) n += 1;
  if (f.tableContains.trim()) n += 1;
  if (f.piiOnly) n += 1;
  return n;
}

function applyFilters(events: Event[], f: StreamFilters): Event[] {
  if (countActiveFilters(f) === 0) return events;
  const actorNeedle = f.actorContains.trim().toLowerCase();
  const tableNeedle = f.tableContains.trim().toLowerCase();
  return events.filter((e) => {
    if (f.decisions.size > 0 && !f.decisions.has(e.decision)) return false;
    if (actorNeedle && !e.actor.toLowerCase().includes(actorNeedle)) return false;
    if (tableNeedle && !e.table.toLowerCase().includes(tableNeedle)) return false;
    if (f.piiOnly && e.pii.length === 0) return false;
    return true;
  });
}

export function ActivityStream() {
  const {
    events: sseEvents,
    paused,
    setPaused,
    connected,
  } = useSseStream<ActivityEventRowDto>({
    url: '/api/v1/activity/events/stream',
    bufferSize: 50,
    acceptEventName: (name) => name.startsWith('decision.'),
  });
  const allEvents: Event[] = sseEvents.map((e) => toViewModel(e.data, e.id));

  // ── Filter state + derived view (Tier UX-fix 2026-05-26) ────────
  const [filters, setFilters] = useState<StreamFilters>(EMPTY_FILTERS);
  const events = useMemo(() => applyFilters(allEvents, filters), [allEvents, filters]);
  const activeFilterCount = countActiveFilters(filters);

  function toggleDecision(d: 'allow' | 'mask' | 'step_up' | 'block'): void {
    setFilters((prev) => {
      const next = new Set(prev.decisions);
      if (next.has(d)) next.delete(d);
      else next.add(d);
      return { ...prev, decisions: next };
    });
  }

  function resetFilters(): void {
    setFilters(EMPTY_FILTERS);
  }

  return (
    <PageContainer>
      <PageHeader
        title="Live Query Stream"
        subtitle="Real-time database activity. Statement text never stored - only SHA-256 hashes. PII categories joined from Discovery classifications."
        badges={[
          { label: 'ClickHouse 24+', variant: 'glass' },
          {
            label: paused ? 'Paused' : connected ? 'Live' : 'Connecting...',
            variant: paused ? 'glass' : connected ? 'success' : 'glass',
          },
          ...(activeFilterCount > 0
            ? [{ label: `${events.length} of ${allEvents.length} shown`, variant: 'info' as const }]
            : []),
        ]}
        actions={
          <>
            <FocttaCoreBadge label="Actor identity live" />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="glass" size="sm">
                  <Filter className="h-4 w-4" />
                  Filter
                  {activeFilterCount > 0 && (
                    <Badge variant="info" className="ml-1 text-[9px]">{activeFilterCount}</Badge>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-80 p-3 space-y-3">
                <div>
                  <div className="text-xs font-medium mb-1.5">Decision</div>
                  <div className="flex flex-wrap gap-1">
                    {(['allow', 'mask', 'step_up', 'block'] as const).map((d) => {
                      const active = filters.decisions.has(d);
                      return (
                        <button
                          key={d}
                          type="button"
                          onClick={() => toggleDecision(d)}
                          className={`text-[10px] px-2 py-1 rounded border transition-colors ${
                            active
                              ? 'bg-primary text-primary-foreground border-primary'
                              : 'border-border/40 text-muted-foreground hover:text-foreground'
                          }`}
                        >
                          {d}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div>
                  <label className="text-xs font-medium mb-1.5 block">Actor contains</label>
                  <input
                    type="text"
                    value={filters.actorContains}
                    onChange={(e) => setFilters((p) => ({ ...p, actorContains: e.target.value }))}
                    placeholder="e.g. priya.singh"
                    className="w-full glass-lite text-xs rounded px-2 py-1.5 bg-background/40 border border-border/40"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium mb-1.5 block">Table contains</label>
                  <input
                    type="text"
                    value={filters.tableContains}
                    onChange={(e) => setFilters((p) => ({ ...p, tableContains: e.target.value }))}
                    placeholder="e.g. customer"
                    className="w-full glass-lite text-xs rounded px-2 py-1.5 bg-background/40 border border-border/40"
                  />
                </div>
                <label className="flex items-center gap-2 text-xs cursor-pointer">
                  <input
                    type="checkbox"
                    checked={filters.piiOnly}
                    onChange={(e) => setFilters((p) => ({ ...p, piiOnly: e.target.checked }))}
                  />
                  <span>Only show events that touched PII</span>
                </label>
                {activeFilterCount > 0 && (
                  <button
                    type="button"
                    onClick={resetFilters}
                    className="w-full flex items-center justify-center gap-1.5 text-xs text-muted-foreground hover:text-foreground py-1 border-t border-border/40 pt-2"
                  >
                    <X className="h-3 w-3" /> Reset {activeFilterCount} filter{activeFilterCount > 1 ? 's' : ''}
                  </button>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
            <Button variant={paused ? 'default' : 'glass'} size="sm" onClick={() => setPaused(!paused)}>
              {paused ? <><Play className="h-4 w-4" /> Resume</> : <><Pause className="h-4 w-4" /> Pause</>}
            </Button>
          </>
        }
      />

      <GlassCard className="p-0 overflow-hidden">
        <div className="grid grid-cols-12 px-4 py-2 text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border/40 font-medium">
          <div className="col-span-2">Timestamp</div>
          <div className="col-span-2">Actor</div>
          <div className="col-span-1">Verb</div>
          <div className="col-span-3">Table / Hash</div>
          <div className="col-span-2">PII</div>
          <div className="col-span-1 text-right">Rows</div>
          <div className="col-span-1 text-right">Decision</div>
        </div>

        {events.length === 0 && (
          <div className="flex flex-col items-center justify-center py-20 text-muted-foreground">
            <ActivityIcon className="h-12 w-12 opacity-30 mb-3 animate-pulse" />
            <p className="text-sm">
              {activeFilterCount > 0 && allEvents.length > 0
                ? `No events match your ${activeFilterCount} filter${activeFilterCount > 1 ? 's' : ''}. ${allEvents.length} in buffer.`
                : 'Waiting for events...'}
            </p>
            {activeFilterCount > 0 && allEvents.length > 0 && (
              <button
                type="button"
                onClick={resetFilters}
                className="mt-2 text-xs text-primary hover:underline"
              >
                Clear filters
              </button>
            )}
          </div>
        )}

        <div className="max-h-[680px] overflow-y-auto">
          <AnimatePresence initial={false}>
            {events.map((e) => (
              <motion.div
                key={e.id}
                layout
                initial={{ opacity: 0, x: -20, height: 0 }}
                animate={{ opacity: 1, x: 0, height: 'auto' }}
                exit={{ opacity: 0, x: 20, height: 0 }}
                transition={{ duration: 0.22, ease: [0.32, 0.72, 0, 1] }}
                className="grid grid-cols-12 px-4 py-2.5 text-xs items-center border-b border-border/20 hover:bg-muted/30 transition-colors"
                style={e.decision === 'block' ? { background: 'hsl(var(--sev-critical) / 0.05)' } : undefined}
              >
                <div className="col-span-2 font-mono text-[11px] text-muted-foreground">{e.ts}</div>
                <div className="col-span-2 truncate font-mono text-[11px]">{e.actor}</div>
                <div className="col-span-1">
                  <Badge variant={e.type === 'EXPORT' || e.type === 'DELETE' ? 'warning' : e.type === 'INSERT' || e.type === 'UPDATE' ? 'info' : 'glass'} className="text-[9px]">
                    {e.type}
                  </Badge>
                </div>
                <div className="col-span-3 min-w-0">
                  <div className="font-mono text-[11px] truncate">{e.table}</div>
                  <div className="font-mono text-[10px] text-muted-foreground truncate opacity-70">#{e.statementHash}</div>
                </div>
                <div className="col-span-2 flex flex-wrap gap-1">
                  {e.pii.length === 0 ? (
                    <span className="text-[10px] text-muted-foreground italic">no PII</span>
                  ) : (
                    e.pii.map((p) => <Badge key={p} variant="glass" className="text-[9px]">{p}</Badge>)
                  )}
                </div>
                <div className="col-span-1 text-right tabular text-[11px]">{e.rows.toLocaleString('en-IN')}</div>
                <div className="col-span-1 text-right">
                  <Badge
                    variant={e.decision === 'allow' ? 'success' : e.decision === 'block' ? 'critical' : e.decision === 'mask' ? 'info' : 'warning'}
                    className="text-[9px]"
                  >
                    {e.decision}
                  </Badge>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      </GlassCard>
    </PageContainer>
  );
}
